import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  fmtSlot,
  formatRelative,
  formatTs,
  shortId,
  slotTimestampMs,
  type ChainParams,
} from "../lib/format";
import type { BlockStatus, Slot, SlotStatus } from "../lib/types";

export function StatusBadge({ status }: { status: SlotStatus }) {
  const cls =
    status === "final"
      ? "badge-final"
      : status === "candidate"
        ? "badge-candidate"
        : "badge-miss";
  return <span className={`badge ${cls}`}>{status}</span>;
}

export function BlockStatusBadge({ status }: { status: BlockStatus }) {
  const cls =
    status === "final"
      ? "badge-final"
      : status === "seen_candidate"
        ? "badge-candidate"
        : "badge-miss";
  const label = status === "seen_candidate" ? "candidate" : status;
  return <span className={`badge ${cls}`}>{label}</span>;
}

/** Block status dot + label; dot colours match the LiveDag markers. */
export const BLOCK_STATUS: Record<BlockStatus, { label: string; dot: string }> = {
  final: { label: "Final", dot: "bg-[#22c55e]" },
  seen_candidate: { label: "Candidate", dot: "bg-[#3b82f6]" },
  discarded: { label: "Discarded", dot: "bg-muted" },
};

/**
 * Shortened id for lists and tables; the full string is in the tooltip.
 * `whitespace-nowrap` so the ellipsis never becomes a line break.
 */
function ShortId({ full, short }: { full: string; short: string }) {
  return (
    <span className="whitespace-nowrap" title={full}>
      {short}
    </span>
  );
}

export function BlockLink({ id, short = true }: { id: string; short?: boolean }) {
  return (
    <Link to={`/block/${id}`} className="font-mono">
      {short ? (
        <ShortId full={id} short={shortId(id)} />
      ) : (
        <span className="break-all">{id}</span>
      )}
    </Link>
  );
}

export function OpLink({ id, short = true }: { id: string; short?: boolean }) {
  return (
    <Link to={`/op/${id}`} className="font-mono">
      {short ? (
        <ShortId full={id} short={shortId(id)} />
      ) : (
        <span className="break-all">{id}</span>
      )}
    </Link>
  );
}

export function AddrLink({ addr, short = true }: { addr: string; short?: boolean }) {
  return (
    <Link to={`/address/${addr}`} className="font-mono">
      {short ? (
        <ShortId full={addr} short={shortId(addr, 6, 6)} />
      ) : (
        <span className="break-all">{addr}</span>
      )}
    </Link>
  );
}

/** List-page title row: h1 + one-line intro, extra controls on the right. */
export function PageHeader({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end gap-4 mb-6">
      <div className="flex flex-col gap-2 mr-auto">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
        <p className="text-muted text-sm sm:text-base">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

export function LiveBadge({ seconds }: { seconds: number }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-panel border border-border text-xs sm:text-sm text-muted">
      <span className="w-1.5 h-1.5 rounded-full bg-ok" />
      Live · refreshes every {seconds} s
    </span>
  );
}

/** Pill-shaped segmented control. `short` labels replace `label` on phones. */
export function Segmented<K extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { key: K; label: string; short?: string }[];
  value: K;
  onChange: (k: K) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex w-full sm:w-auto gap-1 p-1 rounded-full bg-bg border border-border">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={`flex-1 sm:flex-none h-8 px-3.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
            value === o.key ? "bg-panel2 text-fg" : "text-muted hover:text-fg"
          }`}
        >
          <span className="sm:hidden">{o.short ?? o.label}</span>
          <span className="hidden sm:inline">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Compact "period · tN" pill linking to the slot page. */
export function SlotPill({ slot }: { slot: Slot }) {
  return (
    <Link
      to={`/slot/${slot.period}/${slot.thread}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-panel2 text-fg font-mono text-xs no-underline whitespace-nowrap"
    >
      {slot.period.toLocaleString("en-US")}
      <span className="text-muted/60">·</span>t{slot.thread}
    </Link>
  );
}

export function SlotLink({ slot }: { slot: Slot }) {
  return (
    <Link
      to={`/slot/${slot.period}/${slot.thread}`}
      className="font-mono whitespace-nowrap"
      title={`slot (period ${slot.period}, thread ${slot.thread})`}
    >
      {fmtSlot(slot)}
    </Link>
  );
}

/**
 * Slot reference: link using (period, thread) + the deterministic derived
 * wall-clock timestamp of the slot. Use this instead of bare slot numbers.
 */
export function SlotRef({
  slot,
  params,
  timeMode = "relative",
}: {
  slot: Slot;
  params: ChainParams;
  /** "relative" = "5s ago", "absolute" = "2026-04-22 15:30:00", "both" = both */
  timeMode?: "relative" | "absolute" | "both" | "none";
}) {
  const ts = slotTimestampMs(slot, params);
  const rel = formatRelative(ts);
  const abs = formatTs(ts);
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <SlotLink slot={slot} />
      {timeMode !== "none" && (
        <span className="text-muted text-xs whitespace-nowrap" title={abs}>
          ·{" "}
          {timeMode === "absolute"
            ? abs
            : timeMode === "both"
              ? `${rel} (${abs})`
              : rel}
        </span>
      )}
    </span>
  );
}

/** Deterministic timestamp display for a slot (no link). */
export function SlotTimestamp({
  slot,
  params,
  mode = "relative",
}: {
  slot: Slot;
  params: ChainParams;
  mode?: "relative" | "absolute" | "both";
}) {
  const ts = slotTimestampMs(slot, params);
  if (mode === "absolute") return <span title={`${ts} ms`}>{formatTs(ts)}</span>;
  if (mode === "both")
    return (
      <span title={`${ts} ms`}>
        {formatTs(ts)} <span className="text-muted">({formatRelative(ts)})</span>
      </span>
    );
  return <span title={formatTs(ts)}>{formatRelative(ts)}</span>;
}

export function Panel({ title, children, action }: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="card">
      <header className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-base sm:text-lg font-semibold">{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}

export function Loading({ msg = "Loading…" }: { msg?: string }) {
  return <div className="text-muted text-sm">{msg}</div>;
}

export function ErrorMsg({ err }: { err: unknown }) {
  const msg =
    err instanceof Error ? err.message : typeof err === "string" ? err : "Error";
  return <div className="text-bad text-sm">Error: {msg}</div>;
}

export function NotFound({ what }: { what: string }) {
  return <div className="text-muted">No {what} found.</div>;
}

/**
 * Pretty renderer for the SlotCompleteness bitmap. Each flag becomes a
 * small pill; present flags are coloured by topic, missing flags are muted.
 * Tooltips explain in plain terms what each ingestion stage means.
 */
export function CompletenessBadges({
  c,
}: {
  c: {
    block_body_stored: boolean;
    exec_output_final: boolean;
    exec_output_candidate: boolean;
    transfers_stored: boolean;
  };
}) {
  const items: { label: string; ok: boolean; className: string; hint: string }[] =
    [
      {
        label: "block body",
        ok: c.block_body_stored,
        className: "bg-ingest-body/15 text-ingest-body border-ingest-body/30",
        hint: "The full block content (header, operations, endorsements) has been indexed.",
      },
      {
        label: "execution (final)",
        ok: c.exec_output_final,
        className: "bg-ingest-final/20 text-ingest-final border-ingest-final/40",
        hint: "The slot's execution outputs were observed in the finalised ledger.",
      },
      {
        label: "execution (candidate)",
        ok: c.exec_output_candidate,
        className: "bg-ingest-candidate/15 text-ingest-candidate border-ingest-candidate/30",
        hint: "The slot's execution outputs were observed in the candidate (not yet finalised) ledger.",
      },
      {
        label: "transfers",
        ok: c.transfers_stored,
        className: "bg-ingest-transfers/15 text-ingest-transfers border-ingest-transfers/30",
        hint: "Coin and roll transfers triggered in this slot have been decoded and stored.",
      },
    ];
  return (
    <div
      className="flex flex-wrap gap-1.5"
      title="Each pill shows whether a category of slot data has been fully ingested by the indexer. Greyed-out pills mean that part is still missing (e.g. peer or AWS history not yet backfilled)."
    >
      {items.map((it) => (
        <span
          key={it.label}
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border ${
            it.ok
              ? it.className
              : "bg-panel text-muted border-border opacity-60"
          }`}
          title={it.hint}
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${
              it.ok ? "bg-current" : "bg-muted/40"
            }`}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/** Simple key/value row helper for detail pages. Wraps label and value cells. */
export function KV({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** Small pill rendering an operation kind (`transaction`, `call_sc`, …). */
const KIND_CLS: Record<string, string> = {
  transaction: "text-accent2 bg-accent2/10",
  call_sc: "text-op-call bg-op-call/10",
  execute_sc: "text-warn bg-warn/10",
  roll_buy: "text-ok bg-ok/10",
  roll_sell: "text-op-roll-sell bg-op-roll-sell/10",
};

export function OpKindPill({ kind }: { kind: string }) {
  return (
    <span
      className={`inline-block whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${
        KIND_CLS[kind] ?? "text-muted bg-panel2"
      }`}
    >
      {kind.replace("_", " ")}
    </span>
  );
}
